// skipcq: JS-W1028
import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  COMPARISON_FILTER_ALL,
  COMPARISON_FILTER_APPLY_ERRORS,
  COMPARISON_FILTER_CHANGED,
  COMPARISON_FILTER_INVALID,
  COMPARISON_FILTER_MISSING,
  COMPARISON_FILTER_NEEDS_REVIEW,
  COMPARISON_FILTER_NEW,
  comparisonClassificationColor,
  comparisonClassificationLabel,
  comparisonCandidateCurrentName,
  comparisonSummaryCounts,
} from '../../utils/brewery/beerIngestionComparison';
import { ComparisonFieldChangeChips } from './ComparisonFieldChanges';
import { reviewProgressLabel, snapshotBeerForComparisonRow } from '../../utils/brewery/beerIngestionReview';
import useBeerIngestionReviewWorkspace from '../../hooks/useBeerIngestionReviewWorkspace';
import BeerIngestionComparisonTable from './BeerIngestionComparisonTable';
import BeerIngestionReviewFooter from './BeerIngestionReviewFooter';
import BeerIngestionSnapshotTable from './BeerIngestionSnapshotTable';
import ImportSummaryView from './findBeer/ImportSummaryView';
import { SnapshotStatusChip } from './snapshotBeerTableCells';

function ComparisonSummaryChips({ counts, decidedCount, pendingCount }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
      <Chip size="small" color="info" label={`New ${counts.newCount}`} />
      <Chip size="small" color="warning" label={`Changed ${counts.changedCount}`} />
      <Chip size="small" variant="outlined" label={`Missing ${counts.missingCount}`} />
      <Chip size="small" color="error" label={`Invalid ${counts.invalidCount}`} />
      <Chip size="small" color="success" variant="outlined" label={`Unchanged ${counts.unchangedCount}`} />
      {pendingCount != null ? (
        <Chip size="small" label={reviewProgressLabel(decidedCount, pendingCount)} />
      ) : null}
    </Box>
  );
}

ComparisonSummaryChips.propTypes = {
  counts: PropTypes.object.isRequired,
  decidedCount: PropTypes.number,
  pendingCount: PropTypes.number,
};

function ComparisonFilterBar({ filter, onFilterChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={filter}
      onChange={onFilterChange}
      aria-label="Comparison candidate filter"
      sx={{ mb: 1, flexWrap: 'wrap' }}
    >
      <ToggleButton value={COMPARISON_FILTER_NEEDS_REVIEW}>Needs review</ToggleButton>
      <ToggleButton value={COMPARISON_FILTER_ALL}>All</ToggleButton>
      <ToggleButton value={COMPARISON_FILTER_NEW}>New</ToggleButton>
      <ToggleButton value={COMPARISON_FILTER_CHANGED}>Changed</ToggleButton>
      <ToggleButton value={COMPARISON_FILTER_MISSING}>Missing</ToggleButton>
      <ToggleButton value={COMPARISON_FILTER_INVALID}>Invalid</ToggleButton>
      <ToggleButton value={COMPARISON_FILTER_APPLY_ERRORS}>Apply errors</ToggleButton>
    </ToggleButtonGroup>
  );
}

ComparisonFilterBar.propTypes = {
  filter: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
};

function ReviewWorkspaceBody({
  workspace,
  filteredCandidates,
  snapshotBeers,
}) {
  const bulkBusy = workspace.actionLoading;
  return (
    <>
      {workspace.actionError ? (
        <Alert severity="error" sx={{ mb: 1 }}>{workspace.actionError}</Alert>
      ) : null}
      {filteredCandidates.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No candidates match this filter.
        </Typography>
      ) : (
        <BeerIngestionComparisonTable
          candidates={filteredCandidates}
          snapshotBeers={snapshotBeers}
          reviewMode
          selectableKeys={workspace.selectableKeys}
          selectedKeys={workspace.selectedKeys}
          decisions={workspace.decisions}
          reviewLocked={workspace.reviewLocked}
          actionLoading={bulkBusy}
          loadingCandidateKey={workspace.loadingCandidateKey}
          loadingDecisionAction={workspace.loadingDecisionAction}
          headerChecked={workspace.headerCheckboxState.checked}
          headerIndeterminate={workspace.headerCheckboxState.indeterminate}
          assigningStyleName={workspace.assigningStyleName}
          assigningStyleType={workspace.assigningStyleType}
          styleTypes={workspace.styleTypes}
          getDraft={workspace.getDraft}
          getStyleTypeCaption={workspace.getStyleTypeCaption}
          isStyleMapped={workspace.isStyleMapped}
          onToggleSelectAll={workspace.handleToggleSelectAll}
          onToggleSelected={workspace.handleToggleSelected}
          onDecisionClick={workspace.handleDecisionClick}
          onDraftChange={workspace.handleDraftChange}
          onToggleEdit={workspace.handleToggleRowEdit}
          editingKeys={workspace.editingKeys}
          onAssignStyleType={workspace.handleAssignStyleType}
        />
      )}
      <BeerIngestionReviewFooter
        pendingCount={workspace.pendingCount}
        selectedCount={workspace.selectedCount}
        bulkEligibleCount={workspace.bulkEligibleKeys.length}
        selectedBulkEligibleCount={workspace.selectedBulkEligibleCount}
        selectedRejectableCount={workspace.selectedRejectableCount}
        reviewLocked={workspace.reviewLocked}
        reviewApplied={workspace.reviewApplied}
        approvedCount={workspace.approvedCount}
        actionLoading={bulkBusy}
        applying={workspace.applying}
        applyRetryable={workspace.applyRetryable}
        applyBlocked={workspace.applyBlocked}
        unmappedStylesMessage={workspace.unmappedStylesMessage}
        onSelectEligible={workspace.handleSelectEligible}
        onBulkApprove={workspace.handleBulkApprove}
        onBulkReject={workspace.handleBulkReject}
        onComplete={workspace.handleComplete}
        onApply={workspace.handleApply}
      />
      {workspace.importResults ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Apply results</Typography>
          <ImportSummaryView importResults={workspace.importResults} />
        </Box>
      ) : null}
    </>
  );
}

ReviewWorkspaceBody.propTypes = {
  workspace: PropTypes.object.isRequired,
  filteredCandidates: PropTypes.arrayOf(PropTypes.object).isRequired,
  snapshotBeers: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default function BeerIngestionComparisonPanel({
  run,
  snapshot,
  comparison,
  review = null,
  breweryId,
  onReviewUpdated,
  onApplySuccess,
}) {
  if (!run || run.runKind !== 'extraction') {
    return null;
  }

  if (!snapshot) {
    return null;
  }

  if (!run.hasComparison && !comparison) {
    if (run.comparisonErrorMessage) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Comparison unavailable: {run.comparisonErrorMessage}
        </Alert>
      );
    }
    return null;
  }

  return (
    <BeerIngestionComparisonPanelBody
      key={run.id}
      run={run}
      snapshot={snapshot}
      comparison={comparison}
      review={review}
      breweryId={breweryId}
      onReviewUpdated={onReviewUpdated}
      onApplySuccess={onApplySuccess}
    />
  );
}

function BeerIngestionComparisonPanelBody({
  run,
  snapshot,
  comparison,
  review,
  breweryId,
  onReviewUpdated,
  onApplySuccess,
}) {
  const [showFullSnapshot, setShowFullSnapshot] = useState(false);
  const beers = useMemo(
    () => (Array.isArray(snapshot?.beers) ? snapshot.beers : []),
    [snapshot],
  );
  const counts = useMemo(
    () => comparisonSummaryCounts(comparison ?? run ?? {}),
    [comparison, run],
  );
  const workspace = useBeerIngestionReviewWorkspace({
    run,
    snapshot,
    comparison,
    review,
    breweryId,
    onReviewUpdated,
    onApplySuccess,
  });

  const handleToggleSnapshot = useCallback(() => {
    setShowFullSnapshot((visible) => !visible);
  }, []);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Snapshot changes</Typography>
      <ComparisonSummaryChips
        counts={counts}
        decidedCount={workspace.decidedCount}
        pendingCount={workspace.pendingCount}
      />
      <ComparisonFilterBar filter={workspace.filter} onFilterChange={workspace.handleFilterChange} />
      <ReviewWorkspaceBody
        workspace={workspace}
        filteredCandidates={workspace.visibleCandidates}
        snapshotBeers={beers}
      />
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          color="primary"
          component="button"
          type="button"
          onClick={handleToggleSnapshot}
          sx={{ cursor: 'pointer', border: 0, background: 'none', p: 0 }}
        >
          {showFullSnapshot ? 'Hide full snapshot' : 'Show full snapshot'}
        </Typography>
        <Collapse in={showFullSnapshot}>
          <Box sx={{ mt: 1 }}>
            <BeerIngestionSnapshotTable beers={beers} />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

BeerIngestionComparisonPanel.propTypes = {
  run: PropTypes.object,
  snapshot: PropTypes.object,
  comparison: PropTypes.object,
  review: PropTypes.object,
  breweryId: PropTypes.string,
  onReviewUpdated: PropTypes.func,
  onApplySuccess: PropTypes.func,
};

BeerIngestionComparisonPanelBody.propTypes = {
  run: PropTypes.object.isRequired,
  snapshot: PropTypes.object,
  comparison: PropTypes.object,
  review: PropTypes.object,
  breweryId: PropTypes.string,
  onReviewUpdated: PropTypes.func,
  onApplySuccess: PropTypes.func,
};

// Exported for tests that need row rendering without the full panel tree.
export function ComparisonCandidatePreview({ candidate, snapshotBeers = [] }) {
  const snapshotBeer = snapshotBeerForComparisonRow(candidate, snapshotBeers);
  const reason = typeof candidate.decisionReason === 'string'
    ? candidate.decisionReason.trim()
    : '';
  const classificationChip = (
    <Chip
      size="small"
      color={comparisonClassificationColor(candidate.classification)}
      label={comparisonClassificationLabel(candidate.classification)}
    />
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
        {reason ? (
          <Tooltip title={reason} arrow>
            <span>{classificationChip}</span>
          </Tooltip>
        ) : classificationChip}
        <SnapshotStatusChip beer={snapshotBeer} />
      </Box>
      <Typography variant="body2" component="span">
        {comparisonCandidateCurrentName(candidate)}
      </Typography>
      <ComparisonFieldChangeChips candidate={candidate} emptyLabel="" />
    </Box>
  );
}

ComparisonCandidatePreview.propTypes = {
  candidate: PropTypes.object.isRequired,
  snapshotBeers: PropTypes.arrayOf(PropTypes.object),
};
